import React from 'react';
import {
  Building2,
  RefreshCw,
  AlertCircle,
  Package,
  X
} from 'lucide-react';

const PropertyDetailModal = ({
  selectedProperty,
  assets,
  loadingAssets,
  assetsError,
  getPropertyImageUrl,
  getStatusColor,
  formatGLA,
  API,
  onClose
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <div className="flex items-center space-x-3">
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedProperty.buildingName || selectedProperty.building_name || 'Property Details'}
                </h2>
                {selectedProperty.user_added && (
                  <span className="inline-flex px-3 py-1 text-sm font-medium rounded-full bg-blue-100 text-blue-800">
                    User Added
                  </span>
                )}
              </div>
              <p className="text-gray-600">{selectedProperty.sector}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Property Information */}
            <div className="space-y-6">
              {/* Property Image */}
              {getPropertyImageUrl(selectedProperty) && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Property Image</h3>
                  <div className="w-full h-64 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                    <img
                      src={getPropertyImageUrl(selectedProperty)}
                      alt={selectedProperty.buildingName || selectedProperty.building_name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentNode.innerHTML = `<div class="flex items-center justify-center h-full"><div class="text-gray-400">📷</div></div>`;
                      }}
                    />
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Property Details</h3>
                <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-1 gap-3">
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600 font-medium">Building Name:</span>
                      <span className="font-semibold text-gray-900">
                        {selectedProperty.buildingName || selectedProperty.building_name || 'N/A'}
                      </span>
                    </div>

                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600 font-medium">Sector:</span>
                      <span className="font-semibold text-gray-900">
                        {selectedProperty.sector || 'N/A'}
                      </span>
                    </div>

                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600 font-medium">GLA (m²):</span>
                      <span className="font-semibold text-gray-900">
                        {formatGLA(selectedProperty.buildingGla || selectedProperty.building_gla)}
                      </span>
                    </div>

                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600 font-medium">Status:</span>
                      <span className={`px-3 py-1 text-sm rounded-full font-medium ${getStatusColor(selectedProperty.status)}`}>
                        {selectedProperty.status || 'N/A'}
                      </span>
                    </div>

                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600 font-medium">Sub Category:</span>
                      <span className="font-semibold text-gray-900">
                        {selectedProperty.subCategoryString || selectedProperty.sub_category || 'N/A'}
                      </span>
                    </div>

                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600 font-medium">City:</span>
                      <span className="font-semibold text-gray-900">
                        {selectedProperty.city || 'N/A'}
                      </span>
                    </div>

                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600 font-medium">Province:</span>
                      <span className="font-semibold text-gray-900">
                        {selectedProperty.province || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information and Actions */}
            <div className="space-y-6">
              {/* Contact Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  {selectedProperty.buildingContactableList && selectedProperty.buildingContactableList.length > 0 ? (
                    <>
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600 font-medium">Contact Name:</span>
                        <span className="font-semibold text-gray-900">
                          {selectedProperty.buildingContactableList[0].name || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between py-2">
                        <span className="text-gray-600 font-medium">Contact Email:</span>
                        <span className="font-semibold text-gray-900">
                          {selectedProperty.buildingContactableList[0].email ? (
                            <a
                              href={`mailto:${selectedProperty.buildingContactableList[0].email}`}
                              className="text-cyan-600 hover:text-cyan-800 underline"
                            >
                              {selectedProperty.buildingContactableList[0].email}
                            </a>
                          ) : 'N/A'}
                        </span>
                      </div>
                    </>
                  ) : (
                    // Fallback to legacy contact fields
                    <>
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600 font-medium">Contact Name:</span>
                        <span className="font-semibold text-gray-900">
                          {selectedProperty.contact_name || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between py-2">
                        <span className="text-gray-600 font-medium">Contact Email:</span>
                        <span className="font-semibold text-gray-900">
                          {selectedProperty.contact_email ? (
                            <a
                              href={`mailto:${selectedProperty.contact_email}`}
                              className="text-cyan-600 hover:text-cyan-800 underline"
                            >
                              {selectedProperty.contact_email}
                            </a>
                          ) : 'N/A'}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Assets Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Package className="h-5 w-5 text-gray-600" />
                  Assets ({assets.length})
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  {loadingAssets ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="h-6 w-6 text-gray-400 animate-spin" />
                      <span className="ml-2 text-gray-600">Loading assets...</span>
                    </div>
                  ) : assetsError ? (
                    <div className="flex items-center justify-center py-8 text-red-600">
                      <AlertCircle className="h-5 w-5 mr-2" />
                      <span>{assetsError}</span>
                    </div>
                  ) : assets.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Package className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      <p>No assets assigned to this property</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {assets.map((asset, index) => (
                        <div key={asset.id || index} className="bg-white p-3 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium text-gray-900 truncate">
                                  {asset.name || 'Unnamed Asset'}
                                </h4>
                                {asset.asset_tag && (
                                  <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                                    {asset.asset_tag}
                                  </span>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                                <div>
                                  <span className="font-medium">Category:</span> {asset.category || 'N/A'}
                                </div>
                                <div>
                                  <span className="font-medium">Status:</span>
                                  <span className={`ml-1 px-2 py-0.5 text-xs rounded-full ${
                                    asset.status === 'active' ? 'bg-green-100 text-green-800' :
                                    asset.status === 'inactive' ? 'bg-red-100 text-red-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {asset.status || 'Unknown'}
                                  </span>
                                </div>
                                <div className="col-span-2">
                                  <span className="font-medium">Location:</span> {asset.location || 'Not specified'}
                                </div>
                              </div>
                            </div>
                            {asset.image_metadata && asset.image_metadata.length > 0 && (
                              <div className="ml-3 flex-shrink-0">
                                <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                                  <img
                                    src={`${API}${asset.image_metadata[0].url}`}
                                    alt={asset.name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.parentNode.innerHTML = '<div class="text-gray-400 text-xs">📷</div>';
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons - Hide for Retail Manager who can only view */}
              {/* Note: Retail Manager should only VIEW properties, no editing allowed */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  <span className="font-medium text-blue-800">Property View</span>
                </div>
                <p className="text-sm text-blue-700">
                  You are viewing this property in read-only mode. Contact your administrator if you need to make changes.
                </p>
              </div>
            </div>
          </div>

          {/* Close Button */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetailModal;
